import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import EmployeeLayout from '../components/EmployeeLayout';
import { employeePortalAPI } from '../utils/api';
import type { EmployeeNotification } from '../types/employeePortal';
import { formatActionTypeLabel } from '../utils/formatActionType';

const alertColor = (level?: string | null) => {
  switch (level) {
    case 'red':
      return 'var(--an-error)';
    case 'yellow':
      return 'var(--an-warning)';
    case 'green':
      return 'var(--an-success)';
    default:
      return 'var(--an-primary)';
  }
};

const EmployeeNotifications: React.FC = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'employeePortal' });
  const [notifications, setNotifications] = useState<EmployeeNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    employeePortalAPI.getNotifications({ page, per_page: 15 })
      .then(response => {
        if (response.success) {
          setNotifications(response.data.notifications || []);
          setTotalPages(response.data.pagination?.last_page || 1);
        }
      })
      .catch((error) => {
        if (error.response?.status === 404) setAvailable(false);
      })
      .finally(() => setLoading(false));
  }, [page]);

  const emptyState = (
    <Card
      elevation={0}
      sx={{
        border: '1px solid var(--an-border-light)',
        borderRadius: 'var(--an-radius-md)',
        boxShadow: 'var(--an-shadow-sm)',
        backgroundColor: 'var(--an-surface)',
      }}
    >
      <CardContent sx={{ py: 6, textAlign: 'center' }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: 'auto',
            mb: 2,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--an-radius-md)',
            color: 'var(--an-primary)',
            backgroundColor: 'var(--an-primary-light)',
          }}
        >
          <NotificationsIcon />
        </Box>
        <Typography sx={{ color: 'var(--an-text-secondary)', fontWeight: 700 }}>{t('noNotifications')}</Typography>
      </CardContent>
    </Card>
  );

  return (
    <EmployeeLayout>
      <Box sx={{ display: 'grid', gap: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--an-text-primary)', mb: 0.5 }}>
              {t('notifications')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--an-text-secondary)' }}>
              {notifications.length} {t('notifications')}
            </Typography>
          </Box>
          {totalPages > 1 && (
            <Pagination count={totalPages} page={page} onChange={(_, next) => setPage(next)} color="primary" />
          )}
        </Box>

        {!available ? (
          <Alert severity="info" sx={{ borderRadius: 'var(--an-radius-md)', fontWeight: 700 }}>{t('noNotifications')}</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          emptyState
        ) : (
          <Stack spacing={1.5}>
            {notifications.map(notification => (
              <Card
                key={notification.id}
                elevation={0}
                sx={{
                  border: '1px solid var(--an-border-light)',
                  borderRadius: 'var(--an-radius-md)',
                  boxShadow: 'var(--an-shadow-sm)',
                  backgroundColor: 'var(--an-surface)',
                  transition: 'transform var(--an-transition), box-shadow var(--an-transition), border-color var(--an-transition)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    borderColor: 'rgb(var(--an-primary-rgb) / 0.28)',
                    boxShadow: 'var(--an-shadow-md)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        mt: 0.75,
                        borderRadius: '50%',
                        backgroundColor: alertColor(notification.alert_level),
                        boxShadow: `0 0 0 4px rgb(var(--an-primary-rgb) / 0.08)`,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip size="small" label={notification.alert_level || 'info'} variant="outlined" />
                        <Chip size="small" label={formatActionTypeLabel(notification.action_type)} variant="outlined" />
                      </Box>
                      <Typography variant="h6" sx={{ color: 'var(--an-text-primary)', fontWeight: 800, mb: 0.5 }}>
                        {notification.title || notification.policy_name}
                      </Typography>
                      <Typography sx={{ color: 'var(--an-text-secondary)', mb: 1 }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--an-text-tertiary)', fontWeight: 700 }}>
                        {notification.created_at}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(_, next) => setPage(next)} color="primary" />
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </EmployeeLayout>
  );
};

export default EmployeeNotifications;
